import { cva } from 'class-variance-authority';
import classnames from 'classnames';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Select, { SingleValue } from 'react-select';

import { ListType } from 'types/selectbox';
import Label from '../Label';

import { ChevronDownOutlineIcon } from 'assets/svgs';

interface Props {
  className?: string;
  list: ListType[];
  label: string;
  labelTooltip?: string;
  bgColor?: 'layer1' | 'layer2' | 'layer3';
  value?: number | string | undefined;
  placeholder?: string;
  setValue?: Dispatch<SetStateAction<any | undefined>>;
  onChange?: (value: any) => void;
  isDisabled?: boolean;
}

const colorVariants = cva('', {
  variants: {
    bgColor: {
      layer1: '!bg-bg-layer1',
      layer2: '!bg-bg-layer2',
      layer3: '!bg-bg-layer3'
    }
  }
});

const SelectBox = ({
  className,
  list,
  label,
  labelTooltip,
  bgColor = 'layer1',
  placeholder,
  value,
  setValue,
  onChange,
  isDisabled
}: Props) => {
  const [innerValue, setInnerValue] = useState<ListType | null>(list.find((v) => v.value === value) ?? null);

  function handleChange(newValue: SingleValue<ListType>) {
    setInnerValue(newValue);
    if (setValue) {
      setValue(newValue ? newValue.value : undefined);
    }
    if (onChange) {
      onChange(newValue ? newValue.value : undefined);
    }
  }

  useEffect(() => {
    setInnerValue(list.find((v) => v.value === value) ?? null);
  }, [value, list]);

  return (
    <div className={classnames('max-w-80', className)}>
      <Label label={label} labelTooltip={labelTooltip} />
      <Select
        isDisabled={isDisabled}
        value={innerValue}
        isSearchable={false}
        placeholder={placeholder}
        onChange={handleChange}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => (
            <div className="flex h-6 w-6 items-center justify-center">
              <ChevronDownOutlineIcon className="[&_path]:stroke-text-tertiary" />
            </div>
          )
        }}
        classNames={{
          container: () => 'h-8',
          control: () =>
            colorVariants({
              bgColor,
              className: classnames(
                'flex flex-row gap-2 !border-border-primary',
                '!min-h-0 w-full h-full pr-1 rounded border'
              )
            }),
          valueContainer: () => 'px-3 py-[4.5px] h-full text-text-primary font-regular text-body2',
          menu: () =>
            colorVariants({
              bgColor,
              className: classnames('z-10 mt-1 w-full border', 'border-border-primary')
            }),
          option: (state) =>
            classnames(
              '!flex !items-center !text-body2 !font-regular text-text-primary h-8 border-b !px-3 !py-0 [&:last-child]:border-b-0',
              'border-border-primary',
              { 'text-white': state.isFocused }
            )
        }}
        options={list}
      />
    </div>
  );
};

export default SelectBox;